import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
const DEBOUNCE_MS = 1200;

/** One session id per card, shared by every buyer screen — and honoured from
 *  `?s=` so a saved link resumes the SAME half-filled form. */
export const buyerSessionId = (cardId) => {
    const k = `yot_fi_session_${cardId}`;
    const fromLink = new URLSearchParams(window.location.search).get('s');
    if (fromLink) { sessionStorage.setItem(k, fromLink); return fromLink; }
    let s = sessionStorage.getItem(k);
    if (!s) { s = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem(k, s); }
    return s;
};

/** ONE visit id per browser TAB — deliberately NOT per card.
 *
 *  `buyerSessionId` above is per VEHICLE and stays that way: financing,
 *  warranty, insurance and the contact resolver all rely on it being
 *  vehicle-scoped. But three cards scanned on one walk around the lot produce
 *  three unrelated session ids with nothing linking them, which is the actual
 *  problem — so this second, wider id links them.
 *
 *  sessionStorage, not localStorage, and that is the whole point: a visit must
 *  survive navigating between vehicles and END when the tab genuinely closes.
 *  In localStorage a shopper who came back next week would still be "in" last
 *  week's visit.
 */
export const buyerVisitId = () => {
    const k = 'yot_visit_id';
    let v = sessionStorage.getItem(k);
    if (!v) { v = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem(k, v); }
    return v;
};

/** ONE buyer identity per visit. Resolves whatever this session has already
 *  given on ANY screen — the sales thread, a test drive, a saved draft, a
 *  financing application — so no form asks for the same details twice. */
export const useSessionContact = (cardId, sessionId) => {
    const [contact, setContact] = useState(null);
    useEffect(() => {
        let alive = true;
        if (!cardId || !sessionId) return undefined;
        axios.get(`${API}/api/vehicle/${cardId}/session-contact`, { params: { session_id: sessionId } })
            .then((r) => { if (alive) setContact(r.data?.contact || {}); })
            .catch(() => { if (alive) setContact({}); });
        return () => { alive = false; };
    }, [cardId, sessionId]);
    return contact;
};

/** Fill only the fields the buyer has not typed on THIS screen — their current
 *  edit always wins over anything remembered. */
export const mergeKnown = (form, known, keys) => {
    if (!known) return form;
    const out = { ...form };
    keys.forEach(([formKey, knownKey]) => {
        if (!String(out[formKey] || '').trim() && known[knownKey]) out[formKey] = known[knownKey];
    });
    return out;
};

/** Save-for-later plumbing for one screen: hands back whatever was typed last
 *  time, and quietly persists what is being typed now. */
export const useFormDraft = (cardId, sessionId, screen) => {
    const [resumed, setResumed] = useState(null);
    const [loading, setLoading] = useState(true);
    const timer = useRef(null);

    useEffect(() => {
        let alive = true;
        axios.get(`${API}/api/vehicle/${cardId}/draft`, { params: { session_id: sessionId } })
            .then((r) => { if (alive) setResumed(r.data?.drafts?.[screen]?.data || null); })
            .catch(() => { if (alive) setResumed(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; if (timer.current) clearTimeout(timer.current); };
    }, [cardId, sessionId, screen]);

    const save = useCallback((data, contact = {}) => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            axios.put(`${API}/api/vehicle/${cardId}/draft`, {
                session_id: sessionId, screen, data,
                name: contact.name || null, email: contact.email || null, phone: contact.phone || null,
            }).catch(() => { /* a lost draft must never interrupt the buyer */ });
        }, DEBOUNCE_MS);
    }, [cardId, sessionId, screen]);

    return { resumed, loading, saveDraft: save };
};
